import ReconnectingWebSocket from "reconnecting-websocket";
import type { CloseEvent as RwsCloseEvent } from "reconnecting-websocket";

type EventCallback = (data: any) => void;

class WebSocketService {
  private static instance: WebSocketService | null = null;
  private socket: ReconnectingWebSocket | null = null;
  private eventListeners: Map<string, EventCallback[]> = new Map();
  private pingInterval: NodeJS.Timeout | null = null;
  private pingTimeout: NodeJS.Timeout | null = null;

  private eventOnOpen: EventCallback | null = null;
  private eventOnClose: EventCallback | null = null;

  private organizationId: number = -1;

  private constructor() {}

  // Helpers for consistent, safe logging
  private ts() {
    return new Date().toISOString();
  }

  private safeUrl(raw: string) {
    try {
      const url = new URL(raw);
      if (url.searchParams.has("token")) {
        const t = url.searchParams.get("token");
        if (t) url.searchParams.set("token", `${t.slice(0, 3)}***`);
      }
      return url.toString();
    } catch {
      return raw;
    }
  }

  static getInstance(): WebSocketService {
    if (!this.instance) {
      this.instance = new WebSocketService();
    }
    return this.instance;
  }

  public setOrganizationId(organizationId: number) {
    this.organizationId = organizationId;
    // console.log(`[WS ${this.ts()}] setOrganizationId ->`, organizationId);
  }

  public connect(url: string) {
    // Prevent duplicate instances. If an instance exists, let it handle reconnections.
    if (this.socket) {
      // console.log(
      //   `[WS ${this.ts()}] connect() skipped: instance already exists`
      // );
      return;
    }

    console.log(`[WS ${this.ts()}] connect() ->`, this.safeUrl(url));
    this.socket = new ReconnectingWebSocket(url);

    this.socket.onopen = () => {
      // console.log(`[WS ${this.ts()}] onopen: connection established`);
      this.startPing();
      this.eventOnOpen?.({ status: true });
    };

    this.socket.onmessage = event => {
      let data: any = null;
      try {
        data = JSON.parse(event.data);
      } catch (e) {
        console.warn(
          `[WS ${this.ts()}] onmessage: non-JSON payload`,
          event.data
        );
        return;
      }

      const eventType = data.type ?? data?.config?.type;
      console.log(`[WS ${this.ts()}] onmessage:`, eventType || "unknown", data);

      // Notify listeners for the specific event type
      if (eventType && this.eventListeners.has(eventType)) {
        const listeners = this.eventListeners.get(eventType) || [];
        listeners.forEach(callback => callback(data));
      }
    };

    this.socket.onclose = (ev: RwsCloseEvent) => {
      // ReconnectingWebSocket will automatically attempt to reconnect.
      // We only stop pinging and notify UI about temporary disconnection.
      this.stopPing();
      // console.log(
      //   `[WS ${this.ts()}] onclose: code=${ev.code} reason=${ev.reason || ""}`
      // );
      this.eventOnClose?.({ status: false });
      // Keep this.socket instance; RWS will reconnect internally.
    };

    this.socket.onerror = error => {
      console.error(`[WS ${this.ts()}] onerror:`, error);
    };
  }

  public setEventOnOpen(callback: EventCallback) {
    this.eventOnOpen = callback;
    // console.log(`[WS ${this.ts()}] setEventOnOpen registered`);
  }

  public setEventOnClose(callback: EventCallback) {
    this.eventOnClose = callback;
    // console.log(`[WS ${this.ts()}] setEventOnClose registered`);
  }

  private startPing() {
    if (this.pingInterval) {
      // console.log(`[WS ${this.ts()}] startPing skipped: already running`);
      return; // avoid duplicate intervals
    }
    console.log(`[WS ${this.ts()}] startPing: initializing interval`);
    this.pingInterval = setInterval(() => {
      if (this.socket?.readyState === WebSocket.OPEN) {
        console.log(
          `[WS ${this.ts()}] Sending ping... orgId=${this.organizationId}`
        );
        this.socket.send(
          JSON.stringify({ type: "ping", organizationId: this.organizationId })
        );

        // this.pingTimeout = setTimeout(() => {
        //   console.log("Server did not respond to ping, closing connection");
        //   this.stopPing();
        //   this.socket?.close();
        // }, 12000);
      }
    }, 10000);
  }

  private stopPing() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
      // console.log(`[WS ${this.ts()}] stopPing: interval cleared`);
    }
    if (this.pingTimeout) {
      clearTimeout(this.pingTimeout);
      this.pingTimeout = null;
      // console.log(`[WS ${this.ts()}] stopPing: timeout cleared`);
    }
  }

  disconnect() {
    // console.log(`[WS ${this.ts()}] disconnect() called`);
    this.stopPing();
    if (this.socket) {
      try {
        // console.log(`[WS ${this.ts()}] closing socket`);
        this.socket.close();
      } finally {
        this.socket = null;
        // console.log(`[WS ${this.ts()}] socket reference cleared`);
      }
    }
  }

  on(eventType: string, callback: EventCallback) {
    if (!this.eventListeners.has(eventType)) {
      this.eventListeners.set(eventType, []);
    }
    const list = this.eventListeners.get(eventType)!;
    list.push(callback);
    // console.log(
    //   `[WS ${this.ts()}] listener added for '${eventType}', total=${list.length}`
    // );
  }

  off(eventType: string, callback: EventCallback) {
    if (this.eventListeners.has(eventType)) {
      const listeners = this.eventListeners.get(eventType) || [];
      const next = listeners.filter(listener => listener !== callback);
      this.eventListeners.set(eventType, next);
      // console.log(
      //   `[WS ${this.ts()}] listener removed for '${eventType}', total=${next.length}`
      // );
    }
  }
}

export default WebSocketService.getInstance();
